import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';

import { feel } from '@kilat/ui';
import type { ContactInquiry } from '@kilat/shared';

import { useAgentId } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { logger } from '../lib/logger.js';
import { leadsRepo } from '../lib/repositories/leads-repo.js';

/**
 * Warm leads routed to this agent (gap G25).
 *
 * A new agent's home screen is otherwise all zeroes — an empty operator list
 * and a flat earnings chart — during exactly the weeks when they decide whether
 * to keep being an agent. This card is the one place that can have something in
 * it on day one, because inbound interest already exists: people fill in the
 * marketing contact form asking to be contacted, and until leads were routed
 * nothing anywhere read those.
 *
 * Three things this deliberately does:
 *
 *   - Says WHY the agent got each lead. "Reserved for you while you are still
 *     working towards your first signup" is a different message from a silent
 *     row appearing, and it is true — the router really does prefer them.
 *   - Warns when the sender wrote in a language the agent does not speak,
 *     rather than letting them ring someone they cannot talk to.
 *   - Gives the EMPTY state a real next action. "No leads" with nothing to do
 *     is the dead end this card exists to remove, so it explains the queue
 *     position and points at the QR code, which is the fastest thing they
 *     control.
 */

type LoadState =
  | { readonly phase: 'loading' }
  | { readonly phase: 'ready'; readonly leads: readonly ContactInquiry[] }
  | { readonly phase: 'error' };

export function WarmLeadsCard() {
  const t = useI18n();
  const agentId = useAgentId();
  const [state, setState] = useState<LoadState>({ phase: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const [saving, setSaving] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!agentId) return;
    setState({ phase: 'loading' });
    try {
      const leads = await leadsRepo.listForAgent(agentId);
      setState({ phase: 'ready', leads });
    } catch (e: unknown) {
      logger.error('warm leads load failed', { error: String(e) });
      setState({ phase: 'error' });
    }
  }, [agentId]);

  useEffect(() => {
    void load();
  }, [load, attempt]);

  async function markContacted(lead: ContactInquiry): Promise<void> {
    feel('tap');
    setSaving(lead.id);
    setSaveError(null);
    try {
      await leadsRepo.update(lead.id, { leadStatus: 'contacted' });
      feel('success');
      setState((prev) =>
        prev.phase === 'ready'
          ? {
              phase: 'ready',
              leads: prev.leads.map((l) =>
                l.id === lead.id ? { ...l, leadStatus: 'contacted' as const } : l,
              ),
            }
          : prev,
      );
    } catch (e: unknown) {
      logger.error('mark contacted failed', { error: String(e), leadId: lead.id });
      setSaveError(t.t('agent.leads.markContactedError'));
    } finally {
      setSaving(null);
    }
  }

  if (!agentId) return null;

  return (
    <section style={cardStyle}>
      <div>
        <h2 style={cardTitle}>{t.t('agent.leads.heading')}</h2>
        <p style={cardHint}>{t.t('agent.leads.subheading')}</p>
      </div>

      {state.phase === 'loading' && (
        <div role="status" aria-live="polite" style={{ display: 'grid', gap: 'var(--space-2)' }}>
          <span style={srOnly}>{t.t('agent.leads.loading')}</span>
          {[0, 1].map((i) => (
            <div
              key={i}
              aria-hidden
              className="k-skeleton"
              style={{ height: 72, borderRadius: 'var(--radius-md)' }}
            />
          ))}
        </div>
      )}

      {state.phase === 'error' && (
        <div role="alert" style={{ display: 'grid', gap: 'var(--space-3)' }}>
          <p style={{ margin: 0 }}>{t.t('agent.leads.loadError')}</p>
          <button
            type="button"
            className="k-press"
            style={primaryButton}
            onClick={() => {
              feel('tap');
              setAttempt((a) => a + 1);
            }}
          >
            {t.t('agent.leads.retry')}
          </button>
        </div>
      )}

      {state.phase === 'ready' && state.leads.length === 0 && (
        <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
          <h3 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
            {t.t('agent.leads.emptyHeading')}
          </h3>
          <p style={{ margin: 0, color: 'var(--color-on-surface-muted)' }}>
            {t.t('agent.leads.emptyBody')}
          </p>
          {/* Never a dead end: the QR code is the fastest thing they control. */}
          <Link to="/code" style={primaryButton} onClick={() => feel('tap')}>
            {t.t('agent.leads.emptyCta')}
          </Link>
        </div>
      )}

      {state.phase === 'ready' && state.leads.length > 0 && (
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'grid',
            gap: 'var(--space-3)',
          }}
        >
          {state.leads.map((lead) => (
            <li key={lead.id} style={leadRow}>
              <div style={{ display: 'grid', gap: 4 }}>
                <span style={{ fontWeight: 600 }}>{lead.name}</span>
                <span style={cardHint}>{lead.message}</span>
              </div>

              {lead.assignmentReason ? (
                <p style={reasonLine}>{t.t(`agent.leads.reason.${lead.assignmentReason}`)}</p>
              ) : null}

              {lead.assignmentLanguageMatch === false ? (
                <p style={warnLine}>{t.t('agent.leads.languageMismatch')}</p>
              ) : null}

              <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                <a href={`mailto:${lead.email}`} style={primaryButton} onClick={() => feel('tap')}>
                  {t.t('agent.leads.emailCta')}
                </a>
                {lead.leadStatus === 'contacted' ? (
                  <span style={contactedChip}>{t.t('agent.leads.contacted')}</span>
                ) : (
                  <button
                    type="button"
                    className="k-press"
                    style={secondaryButton}
                    disabled={saving === lead.id}
                    onClick={() => void markContacted(lead)}
                  >
                    {t.t('agent.leads.markContacted')}
                  </button>
                )}
              </div>
            </li>
          ))}
          {saveError ? (
            <li role="alert" style={warnLine}>
              {saveError}
            </li>
          ) : null}
        </ul>
      )}
    </section>
  );
}

const srOnly: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

const cardStyle: CSSProperties = {
  background: 'var(--color-surface)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--space-5)',
  boxShadow: 'var(--shadow-1)',
  display: 'grid',
  gap: 'var(--space-3)',
};

const cardTitle: CSSProperties = { margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 };

const cardHint: CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
};

const leadRow: CSSProperties = {
  display: 'grid',
  gap: 'var(--space-2)',
  padding: 'var(--space-3)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-surface-2, var(--color-surface))',
  border: '1px solid var(--color-border)',
};

const reasonLine: CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-meta)',
  color: 'var(--color-primary)',
  fontWeight: 600,
};

const warnLine: CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-meta)',
  color: 'var(--color-warning, #b45309)',
};

const primaryButton: CSSProperties = {
  minHeight: 'var(--touch-target)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0 var(--space-4)',
  borderRadius: 'var(--radius-pill)',
  background: 'var(--color-primary)',
  color: 'var(--color-primary-fg)',
  border: 'none',
  fontWeight: 600,
  fontSize: 'var(--text-body)',
  textDecoration: 'none',
  cursor: 'pointer',
};

const secondaryButton: CSSProperties = {
  ...primaryButton,
  background: 'transparent',
  color: 'var(--color-on-surface)',
  border: '1px solid var(--color-border-strong, var(--color-border))',
};

const contactedChip: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '0 var(--space-3)',
  minHeight: 'var(--touch-target)',
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
};
